import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icons } from '../Icons';
import ThemeToggle from '../ui/ThemeToggle';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import { APIService, openSavedReport } from '../../services/api';

const Header = ({ 
  currentUser, 
  onLoginClick, 
  onLogout, 
  onPricingClick, 
  onNewAnalysis,
  onCompareMode,
  onShowDataSourceInfo,
  onNavigateTo,
  onNavigateHome,
  onShowBadges,
  onShowAgency,
  auditSoundEnabled = false,
  onAuditSoundChange,
  showHeaderLogo = true
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language } = useLanguage();
  const isCs = language === 'cs';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const localizedHref = useCallback((path) => {
    if (language !== 'en') return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set('lang', 'en');
    return `${url.pathname}${url.search}${url.hash}`;
  }, [language]);
  const handlePublicNavigation = useCallback((event, page, afterNavigate) => {
    if (
      !onNavigateTo ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) return;
    event.preventDefault();
    onNavigateTo(page, event.currentTarget.getAttribute('href'));
    afterNavigate?.();
  }, [onNavigateTo]);

  return (
    <>
      <div className={`primary-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 flex items-center justify-between gap-2 w-full h-full">
          <div className="flex items-center gap-1 sm:gap-4 min-w-0 shrink-0">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          
          <a
            href={localizedHref('/')}
            aria-label="ControlVitals"
            onClick={(event) => handlePublicNavigation(event, 'home', onNavigateHome)}
            className={`flex items-center gap-3 text-white font-bold hover:opacity-90 transition-all group shrink-0 ${showHeaderLogo ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{ transition: 'opacity 0.3s ease' }}
          >
            <svg className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
            <span className="hidden sm:inline text-lg sm:text-xl font-bold tracking-tight">
              <span className="text-white">Control</span>
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Vitals</span>
            </span>
          </a>
          
          <nav className="hidden md:flex items-center gap-1 nav-desktop-menu">
            {/* NÁSTROJE - Analýza webů */}
            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                {t('nav.tools')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{isCs ? 'Analýza webu' : 'Web Analysis'}</div>
                <a 
                  href={localizedHref('/health-check')}
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Activity size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-emerald-400">{isCs ? 'Domain Health Check' : 'Domain Health Check'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Komplexní zdravotní check webu' : 'Complete website health check'}</div>
                  </div>
                </a>
                <a 
                  href={localizedHref('/')}
                  onClick={(e) => handlePublicNavigation(e, 'home', onNavigateHome)}
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Gauge size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-cyan-400">{isCs ? 'Analyzovat web' : 'Analyze website'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Zadejte URL a spusťte analýzu' : 'Enter a URL and run an analysis'}</div>
                  </div>
                </a>
                <a href={localizedHref('/lab-test')} onClick={(e) => handlePublicNavigation(e, 'lab-test')} className="nav-dropdown-item">
                  <span className="nav-dropdown-item-icon">
                    <Icons.FlaskConical size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-purple-400">{isCs ? 'Lab Test' : 'Lab Test'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Laboratorní testování (Lighthouse)' : 'Laboratory testing (Lighthouse)'}</div>
                  </div>
                </a>
                <a href={localizedHref('/domain-comparison')} onClick={(e) => handlePublicNavigation(e, 'domain-comparison')} className="nav-dropdown-item">
                  <span className="nav-dropdown-item-icon"><Icons.Scale size={16} /></span>
                  <div>
                    <div className="font-medium text-cyan-400">{isCs ? 'Porovnat domény' : 'Compare domains'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Zadejte dvě nebo více domén' : 'Enter two or more domains'}</div>
                  </div>
                </a>
                <div className="nav-dropdown-divider"></div>
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{isCs ? 'Bezplatné nástroje' : 'Free Tools'}</div>
                <a href={localizedHref('/quick-tools')} onClick={(e) => handlePublicNavigation(e, 'quick-tools')} className="nav-dropdown-item bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg my-1">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Zap size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-amber-400">{isCs ? 'Quick Tools' : 'Quick Tools'}</div>
                    <div className="text-xs text-slate-500">{isCs ? '40+ bezplatných nástrojů' : '40+ free tools'}</div>
                  </div>
                </a>
                <a 
                  href={localizedHref('/eshop-health-check')}
                  onClick={(e) => handlePublicNavigation(e, 'eshop-health-check')}
                  className="nav-dropdown-item bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-lg my-1"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Shield size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-emerald-400">{isCs ? 'E-shop Health Check' : 'E-shop Health Check'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'ČOI compliance, feedy, SSL, structured data' : 'Compliance, feeds, SSL, structured data'}</div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* INSIGHTS - Tržní data */}
            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                {isCs ? 'Insights' : 'Insights'}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{isCs ? 'Tržní data' : 'Market Data'}</div>
                <a href={localizedHref('/market-insights')} onClick={(e) => handlePublicNavigation(e, 'market-insights')} className="nav-dropdown-item">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Chart size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-blue-400">{isCs ? 'Market Insights' : 'Market Insights'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Evropská data Web Vitals' : 'European Web Vitals data'}</div>
                  </div>
                </a>
                <a href={localizedHref('/czech-webs-ranking')} onClick={(e) => handlePublicNavigation(e, 'czech-webs-ranking')} className="nav-dropdown-item">
                  <span className="nav-dropdown-item-icon">
                    🇨🇿
                  </span>
                  <div>
                    <div className="font-medium text-blue-400">{isCs ? 'Žebříček CZ webů' : 'Czech Webs Ranking'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Porovnání českých webů' : 'Compare Czech websites'}</div>
                  </div>
                </a>
                <a 
                   href={localizedHref('/web-core-vitals-google-report-crux')}
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Globe size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'CrUX Dashboard' : 'CrUX Dashboard'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Alternativa ke Google CrUX' : 'Alternative to Google CrUX'}</div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* MONITORING */}
            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                {isCs ? 'Monitoring' : 'Monitoring'}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <a 
                  href={localizedHref('/history-monitoring')}
                  onClick={(e) => handlePublicNavigation(e, 'history-monitoring')}
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.TrendingUp size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-emerald-400">{isCs ? 'Historie & Trendy' : 'History & Trends'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Sledování vývoje metrik' : 'Track metrics over time'}</div>
                  </div>
                </a>
                <a 
                  href={localizedHref('/monitoring')}
                  onClick={(e) => handlePublicNavigation(e, 'monitoring')}
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Bell size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'Alerty & Notifikace' : 'Alerts & Notifications'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Automatické upozornění na problémy' : 'Automatic issue notifications'}</div>
                  </div>
                </a>
                <a 
                  href={localizedHref('/platform-status')}
                  onClick={(e) => handlePublicNavigation(e, 'platform-status')}
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Activity size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-cyan-400">{isCs ? 'Platform Status' : 'Platform Status'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Zdraví ekosystému Google & CDN' : 'Google & CDN ecosystem health'}</div>
                  </div>
                </a>
                {currentUser && (
                  <a href="#" onClick={(e) => { e.preventDefault(); onShowBadges?.(); }} className="nav-dropdown-item">
                    <span className="nav-dropdown-item-icon">
                      <Icons.Award size={16} />
                    </span>
                    <div>
                      <div className="font-medium">{isCs ? 'Live Badges' : 'Live Badges'}</div>
                      <div className="text-xs text-slate-500">{isCs ? 'Zobrazení skóre na webu' : 'Display score on your website'}</div>
                    </div>
                  </a>
                )}
                <div className="nav-dropdown-divider"></div>
                <a href="#" className="nav-dropdown-item opacity-50 cursor-not-allowed">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Code size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'API přístup' : 'API Access'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Již brzy' : 'Coming soon'}</div>
                  </div>
                </a>
              </div>
            </div>

            {/* PRO KOHO */}
            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                {t('nav.forWhom')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <a 
                  href="#revenue-loss-section" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    document.getElementById('revenue-loss-section')?.scrollIntoView({ behavior: 'smooth' });
                  }} 
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.ShoppingCart size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'Pro E-shopy' : 'For E-commerce'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Optimalizace konverzí' : 'Conversion optimization'}</div>
                  </div>
                </a>
                <a 
                  href="#revenue-loss-section" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    document.getElementById('revenue-loss-section')?.scrollIntoView({ behavior: 'smooth' });
                  }} 
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.Newspaper size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'Pro Publishery' : 'For Publishers'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Maximalizace engagementu' : 'Maximize engagement'}</div>
                  </div>
                </a>
                <a href="#" onClick={(e) => { e.preventDefault(); onShowAgency?.(); }} className="nav-dropdown-item">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Users size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'Pro Agentury' : 'For Agencies'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'White-label řešení' : 'White-label solution'}</div>
                  </div>
                </a>
              </div>
            </div>
            
            <a
              href={localizedHref('/pricing')}
              onClick={(e) => handlePublicNavigation(e, 'pricing')}
              className="nav-link"
            >
              {t('nav.pricing')}
            </a>
            
            {/* VZDĚLÁVÁNÍ */}
            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                {t('nav.learn')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="nav-dropdown-menu">
                <a href={localizedHref('/methodology')} onClick={(e) => handlePublicNavigation(e, 'methodology')} className="nav-dropdown-item">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Info size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'Metodika měření' : 'Measurement Methodology'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Vysvětlení metrik Web Vitals' : 'Web Vitals metrics explained'}</div>
                  </div>
                </a>
                <a 
                  href="https://web.dev/vitals/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-dropdown-item"
                >
                  <span className="nav-dropdown-item-icon">
                    <Icons.ExternalLink size={16} />
                  </span>
                  <div>
                    <div className="font-medium">{isCs ? 'Web.dev Dokumentace' : 'Web.dev Documentation'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Oficiální Google dokumentace' : 'Official Google documentation'}</div>
                  </div>
                </a>
                <a href={localizedHref('/ai-crawler-guide')} onClick={(e) => handlePublicNavigation(e, 'ai-crawler-guide')} className="nav-dropdown-item bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg my-1">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Bot size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-purple-400">{isCs ? 'AI Crawler Průvodce' : 'AI Crawler Guide'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Správa GPTBot, Bingbot AI a dalších' : 'Manage GPTBot, Bingbot AI & more'}</div>
                  </div>
                </a>
                <div className="nav-dropdown-divider"></div>
                <a href={localizedHref('/blog')} onClick={(e) => handlePublicNavigation(e, 'blog')} className="nav-dropdown-item bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-lg my-1">
                  <span className="nav-dropdown-item-icon">
                    <Icons.Book size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-blue-400">{isCs ? 'Blog & Magazín' : 'Blog & Magazine'}</div>
                    <div className="text-xs text-slate-500">{isCs ? 'Články o Web Vitals a optimalizaci' : 'Articles on Web Vitals & optimization'}</div>
                  </div>
                </a>
              </div>
            </div>
          </nav>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <ThemeToggle className="shrink-0 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white" />

          <button
            onClick={() => {
              if (onNavigateTo) {
                onNavigateTo('home');
                setTimeout(() => {
                  onNewAnalysis?.();
                  const input = document.querySelector('input[placeholder*="zadejte"], input[placeholder*="Zadejte"], input[type="url"]');
                  if (input) input.focus();
                }, 150);
              } else {
                window.location.href = '/';
              }
            }}
            aria-label={t('nav.newAnalysis')}
            className="nav-cta !px-2 sm:!px-4 shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="hidden sm:inline">{t('nav.newAnalysis')}</span>
          </button>
          
          {currentUser && <AnalysisQueueIndicator userId={currentUser.id} onNavigateTo={onNavigateTo} />}
          <NotificationBell userId={currentUser?.id} onNavigateTo={onNavigateTo}
            auditSoundEnabled={auditSoundEnabled} onAuditSoundChange={onAuditSoundChange} />
          
          {currentUser ? (
            <UserMenu 
              user={currentUser} 
              onLogout={onLogout}
              onOpenPricing={onPricingClick}
            />
          ) : (
            <button
              onClick={onLoginClick}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="hidden sm:inline">{t('nav.login')}</span>
            </button>
          )}
        </div>
      </div>
      </div>
      
      {mobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/80 z-[100] md:hidden backdrop-blur-sm"
            onClick={closeMobileMenu}
          />
          <div className="fixed top-0 left-0 bottom-0 w-72 bg-slate-900 border-r border-slate-700 z-[110] md:hidden animate-slide-in overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                <span className="text-lg font-bold tracking-tight">
                  <span className="text-white">Control</span>
                  <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Vitals</span>
                </span>
              </div>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-slate-400 hover:text-white"
                aria-label={t('nav.closeNavigation')}
              >
                <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-1">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-3">{t('nav.tools')}</div>
              <a 
                href={localizedHref('/health-check')}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Activity size={18} />
                <span>{t('nav.healthCheck')}</span>
              </a>
              <a 
                href={localizedHref('/web-core-vitals-google-report-crux')}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-cyan-400 hover:text-cyan-300 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Globe size={18} />
                <span>{t('nav.cruxAlternative')}</span>
              </a>
              <a 
                href={localizedHref('/history-monitoring')}
                onClick={(e) => handlePublicNavigation(e, 'history-monitoring', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.TrendingUp size={18} className="text-cyan-400" />
                <span>{t('nav.historyMonitoring')}</span>
              </a>
              <a
                href={localizedHref('/domain-comparison')}
                onClick={(e) => handlePublicNavigation(e, 'domain-comparison', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Scale size={18} className="text-cyan-400" />
                <span>{isCs ? 'Porovnat domény' : 'Compare domains'}</span>
              </a>
              <a
                href={localizedHref('/lab-test')}
                onClick={(e) => handlePublicNavigation(e, 'lab-test', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.FlaskConical size={18} className="text-purple-400" />
                <span>Lab Test</span>
              </a>
              <a
                href={localizedHref('/quick-tools')}
                onClick={(e) => handlePublicNavigation(e, 'quick-tools', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Zap size={18} className="text-amber-400" />
                <span>Quick Tools</span>
              </a>
              <a 
                href={localizedHref('/eshop-health-check')}
                onClick={(e) => handlePublicNavigation(e, 'eshop-health-check', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20 rounded-lg transition-colors"
              >
                <Icons.Shield size={18} />
                <span>{isCs ? 'E-shop Health Check' : 'E-shop Health Check'}</span>
              </a>
              <a 
                href={localizedHref('/monitoring')}
                onClick={(e) => handlePublicNavigation(e, 'monitoring', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Bell size={18} className="text-blue-400" />
                <span>{t('nav.monitoring')}</span>
              </a>
              <a 
                href={localizedHref('/platform-status')}
                onClick={(e) => handlePublicNavigation(e, 'platform-status', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Activity size={18} className="text-cyan-400" />
                <span>{isCs ? 'Platform Status' : 'Platform Status'}</span>
              </a>
              
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-3 pt-4">{t('nav.forWhom')}</div>
              <button 
                onClick={() => { 
                  document.getElementById('revenue-loss-section')?.scrollIntoView({ behavior: 'smooth' }); 
                  closeMobileMenu(); 
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.ShoppingCart size={18} className="text-slate-400" />
                <span>{t('nav.forEshops')}</span>
              </button>
              <button 
                onClick={() => { 
                  document.getElementById('revenue-loss-section')?.scrollIntoView({ behavior: 'smooth' }); 
                  closeMobileMenu(); 
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Newspaper size={18} className="text-slate-400" />
                <span>{t('nav.forPublishers')}</span>
              </button>
              <button 
                onClick={() => { onShowAgency?.(); closeMobileMenu(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Users size={18} className="text-slate-400" />
                <span>{t('nav.forAgencies')}</span>
              </button>
              
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-3 pt-4">{t('nav.learn')}</div>
              <a
                href={localizedHref('/methodology')}
                onClick={(e) => handlePublicNavigation(e, 'methodology', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Info size={18} className="text-slate-400" />
                <span>{t('nav.methodology')}</span>
              </a>
              <a
                href={localizedHref('/market-insights')}
                onClick={(e) => handlePublicNavigation(e, 'market-insights', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-purple-900/30 rounded-lg transition-colors"
              >
                <Icons.Chart size={18} className="text-purple-400" />
                <span>{language === 'cs' ? 'Market Insights' : 'Market Insights'}</span>
              </a>
              <a
                href={localizedHref('/ai-crawler-guide')}
                onClick={(e) => handlePublicNavigation(e, 'ai-crawler-guide', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-purple-900/30 rounded-lg transition-colors"
              >
                <Icons.Bot size={18} className="text-purple-400" />
                <span>{language === 'cs' ? 'AI Crawler Průvodce' : 'AI Crawler Guide'}</span>
              </a>
              <a
                href={localizedHref('/blog')}
                onClick={(e) => handlePublicNavigation(e, 'blog', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-blue-900/30 rounded-lg transition-colors"
              >
                <Icons.Book size={18} className="text-blue-400" />
                <span>{language === 'cs' ? 'Blog & Magazín' : 'Blog & Magazine'}</span>
              </a>
              <a
                href={localizedHref('/')}
                onClick={(e) => handlePublicNavigation(e, 'home', () => { onNavigateHome?.(); closeMobileMenu(); })}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Gauge size={18} className="text-slate-400" />
                <span>{isCs ? 'Analyzovat web' : 'Analyze website'}</span>
              </a>
              
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-3 pt-4">{t('nav.pricing')}</div>
              <a
                href={localizedHref('/pricing')}
                onClick={(e) => handlePublicNavigation(e, 'pricing', closeMobileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <Icons.Tag size={18} className="text-emerald-400" />
                <span>{t('nav.pricing')}</span>
              </a>
                          </div>
            
            <div className="p-4 border-t border-slate-700 mt-4">
              <div className="mb-4 flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-1">
                <span className="text-sm text-slate-300">{t('nav.theme')}</span>
                <ThemeToggle className="text-slate-300 hover:text-white" />
              </div>
              {currentUser ? (
                <div className="text-sm text-slate-400">
                  {t('nav.login')} <span className="text-white">{currentUser.email}</span>
                </div>
              ) : (
                <button
                  onClick={() => { onLoginClick?.(); closeMobileMenu(); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('nav.login')}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const UserMenu = ({ user, onLogout, onOpenPricing }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();
  
  if (!user) return null;
  
  const tierColors = {
    free: 'text-slate-400',
    pro: 'text-blue-400',
    business: 'text-purple-400'
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 h-10 rounded-lg bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-slate-600 transition-colors shadow-sm"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
          {(user.name || user.email)[0].toUpperCase()}
        </div>
        <div className="hidden sm:block text-left max-w-[100px]">
          <div className="text-xs text-slate-800 dark:text-white truncate leading-tight">{user.name || user.email.split('@')[0]}</div>
          <div className={`text-[10px] truncate leading-tight ${tierColors[user.tier?.slug] || 'text-slate-500 dark:text-slate-400'}`}>
            {user.tier?.name || 'Free'} • Level {user.level || 1}
          </div>
        </div>
        <svg className="hidden sm:block w-3 h-3 text-slate-500 dark:text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
          <div className="absolute right-0 mt-2 w-64 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl p-2 animate-slide-in">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 mb-2">
              <div className="text-slate-900 dark:text-white font-medium">{user.name || user.email}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                    style={{ width: `${Math.min((user.xpPoints || 0) % 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-slate-500 dark:text-slate-400">{user.xpPoints || 0} XP</span>
              </div>
            </div>
            
            {user.usage && (
              <div className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 mb-2">
                <div className="flex justify-between mb-1">
                  <span>{t('nav.domains')}:</span>
                  <span>{user.usage.trackedOriginsCount} / {user.tier?.maxTrackedOrigins === -1 ? '∞' : user.tier?.maxTrackedOrigins}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('nav.exports')}:</span>
                  <span>{user.usage.exportsCount} / {user.tier?.exportsPerMonth === -1 ? '∞' : user.tier?.exportsPerMonth}</span>
                </div>
              </div>
            )}
            
            {user.is_admin && (
              <button 
                onClick={() => { window.dispatchEvent(new CustomEvent('openAdminPanel')); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-700/50 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Admin panel
              </button>
            )}
            
            <button 
              onClick={() => { window.dispatchEvent(new CustomEvent('openDomainGroups')); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700/50 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              {t('nav.myDomains')}
            </button>
            
            <button 
              onClick={() => { window.dispatchEvent(new CustomEvent('openAnalysisHistory')); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700/50 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('nav.analysisHistory')}
            </button>
            
            <button 
              onClick={() => { window.dispatchEvent(new CustomEvent('openMyAnalyses')); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-amber-50 dark:hover:bg-slate-700/50 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('nav.myAnalyses')}
            </button>
            
            <button 
              onClick={() => { window.dispatchEvent(new CustomEvent('openAlertSettings', { detail: { tab: 'configs' } })); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-purple-50 dark:hover:bg-slate-700/50 hover:text-purple-600 dark:hover:text-purple-400 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {t('nav.monitoring') || 'Monitoring'}
            </button>

            {user.tier?.slug && user.tier.slug !== 'free' && (
              <button 
                onClick={() => { window.dispatchEvent(new CustomEvent('openAlertSettings', { detail: { tab: 'google' } })); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700/50 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {t('nav.googleIntegration') || 'Google integrace'}
              </button>
            )}
            
            {user.tier?.slug && user.tier.slug !== 'free' && !user.is_admin && (
              <button 
                onClick={() => { onOpenPricing?.(); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('nav.subscription')}
              </button>
            )}

            {(user.tier?.slug === 'business' || user.tier?.slug === 'enterprise') && (
              <button 
                onClick={() => { window.dispatchEvent(new CustomEvent('openAiNarrativeSettings')); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-cyan-50 dark:hover:bg-slate-700/50 hover:text-cyan-600 dark:hover:text-cyan-400 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('aiNarratives.menuLabel') || 'AI komentář v exportech'}
              </button>
            )}
            
            <div className="border-t border-slate-200 dark:border-slate-700 mt-2 pt-2">
              <div className="px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                  {t('nav.theme')}
                </div>
                <ThemeToggle className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" />
              </div>
              
              <div className="px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                  {t('nav.language')}
                </div>
                <LanguageSwitcher compact />
              </div>
            </div>
            
            <div className="border-t border-slate-200 dark:border-slate-700 mt-2 pt-2">
              <button 
                onClick={() => { onLogout?.(); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const NotificationBell = ({ userId, onNavigateTo, auditSoundEnabled = false, onAuditSoundChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { t, language } = useLanguage();
  const lastFetchRef = useRef(0);
  const currentUserId = useRef(userId);
  currentUserId.current = userId;

  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    lastFetchRef.current = 0;
  }, [userId]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    if (document.hidden) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 25000) return;
    lastFetchRef.current = now;
    try {
      const response = await APIService.getNotifications();
      if (currentUserId.current === userId && response.notifications) {
        setNotifications(response.notifications);
        setUnreadCount(response.notifications.filter(n => !n.read).length);
      }
    } catch (e) {
      console.warn('Failed to fetch notifications:', e);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    const handleVisibility = () => { if (!document.hidden) fetchNotifications(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await APIService.markNotificationRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.warn('Failed to mark notification as read:', e);
    }
  };

  const handleClick = async (notification) => {
    const healthCheckId = notification.health_check_id || notification.healthCheckId;
    setIsOpen(false);
    if (notification.type === 'analysis_approved') {
      // Open in the click gesture; browsers otherwise block tabs opened after
      // the authenticated resolution/fetch. Never navigate directly to the
      // protected HTML URL (iframe cookies are not reliable).
      const reportTab = window.open('', '_blank');
      if (reportTab) reportTab.opener = null;
      try {
        const { auditId } = await APIService.getNotificationReport(notification.id);
        if (auditId && reportTab) {
          await openSavedReport(auditId, 'audit', reportTab);
          return;
        }
      } catch (error) {
        console.warn('Failed to open approved audit report:', error);
      }
      reportTab?.close();
      window.dispatchEvent(new CustomEvent('openMyAnalyses'));
      return;
    }
    if (notification.type === 'analysis_awaiting_review') {
      window.dispatchEvent(new CustomEvent('openMyAnalyses'));
      return;
    }
    // Preserve existing result links for completed health checks.
    if (healthCheckId) {
      onNavigateTo?.('health-check', `/health-check?id=${encodeURIComponent(healthCheckId)}`);
    } else if (notification.type?.startsWith('analysis_')) {
      window.dispatchEvent(new CustomEvent('openMyAnalyses'));
    }
  };
  
  // Mark all notifications as read when OPENING the popover
  const handleOpenPopover = async () => {
    const wasOpen = isOpen;
    setIsOpen(!isOpen);
    
    // Mark all as read when opening (not closing)
    if (!wasOpen && unreadCount > 0) {
      // Immediately update UI
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      
      // Then sync with server
      try {
        await APIService.markAllNotificationsRead();
      } catch (e) {
        console.warn('Failed to mark all as read:', e);
      }
    }
  };

  return (
    <div className={`relative ${isOpen ? 'z-50' : ''}`}>
      <button
        onClick={handleOpenPopover}
        className="relative z-50 p-2 text-slate-400 hover:text-white transition-colors"
        aria-label={`${t('notifications.title')}, ${t('searchForm.soundWhenComplete')}: ${auditSoundEnabled ? (language === 'cs' ? 'zapnuto' : 'on') : (language === 'cs' ? 'vypnuto' : 'off')}`}
        aria-expanded={isOpen}
        title={`${t('notifications.title')} · ${t('searchForm.soundWhenComplete')}: ${auditSoundEnabled ? (language === 'cs' ? 'zapnuto' : 'on') : (language === 'cs' ? 'vypnuto' : 'off')}`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {auditSoundEnabled && (
          <svg aria-hidden="true" className="absolute top-0 -left-1 w-3 h-3 text-cyan-400" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 4q3 2 0 4M5 2q5 4 0 8" />
          </svg>
        )}
        {userId && unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="fixed left-2 right-2 top-14 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="font-semibold text-slate-900 dark:text-white">{userId ? t('notifications.title') : t('searchForm.soundWhenComplete')}</span>
              {unreadCount > 0 && (
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">{unreadCount} {t('notifications.new')}</span>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700">
              <span id="audit-sound-label">{t('searchForm.soundWhenComplete')}</span>
              <button type="button" role="switch" aria-labelledby="audit-sound-label" aria-checked={auditSoundEnabled}
                onClick={() => onAuditSoundChange?.(!auditSoundEnabled)}
                className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500 ${auditSoundEnabled ? 'bg-cyan-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <span aria-hidden="true" className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${auditSoundEnabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {userId && <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                  {t('notifications.empty')}
                </div>
              ) : (
                notifications.slice(0, 10).map(notification => (
                  <button
                    key={notification.id}
                    onClick={() => handleClick(notification)}
                    className={`w-full px-4 py-3 text-left border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                      !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        notification.type === 'health_check_complete' ? 'bg-green-500/20 text-green-500' : 'bg-blue-500/20 text-blue-500'
                      }`}>
                        {notification.type === 'health_check_complete' ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{notification.title}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{notification.message}</div>
                        <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                          {new Date(notification.created_at).toLocaleString()}
                        </div>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-2" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>}
          </div>
        </>
      )}
    </div>
  );
};

const AnalysisQueueIndicator = ({ userId, onNavigateTo }) => {
  const [analyses, setAnalyses] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const { t } = useLanguage();
  const lastFetchRef = useRef(0);

  const fetchAnalyses = useCallback(async () => {
    if (!userId) return;
    if (document.hidden) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 12000) return;
    lastFetchRef.current = now;
    try {
      const response = await APIService.getMyAnalyses();
      if (response.analyses) {
        const active = response.analyses.filter(a => a.status === 'pending' || a.status === 'running');
        setAnalyses(active.slice(0, 10));
        setActiveCount(active.length);
      }
    } catch (e) {
      console.warn('Failed to fetch analyses:', e);
    }
  }, [userId]);

  useEffect(() => {
    fetchAnalyses();
    const interval = setInterval(fetchAnalyses, 30000);
    const handleVisibility = () => { if (!document.hidden) fetchAnalyses(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [fetchAnalyses]);

  const formatWaitTime = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const getStatusIcon = (status) => {
    if (status === 'running') {
      return (
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      );
    }
    return (
      <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  if (!userId || activeCount === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-white transition-colors"
        aria-label={t('analysisQueue.title')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
          {activeCount > 9 ? '9+' : activeCount}
        </span>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50">
            <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <span className="font-semibold text-slate-900 dark:text-white">{t('analysisQueue.title')}</span>
              <span className="text-xs text-slate-500">{activeCount} {t('analysisQueue.active')}</span>
            </div>
            <div className="p-2 space-y-2">
              {analyses.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">{t('analysisQueue.empty')}</div>
              ) : (
                analyses.map(analysis => (
                  <div
                    key={analysis.id}
                    className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{getStatusIcon(analysis.status)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-900 dark:text-white truncate">
                          {analysis.analysis_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{analysis.url}</div>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-400">
                          {analysis.device && (
                            <span className={`px-1.5 py-0.5 rounded ${
                              analysis.device === 'desktop' ? 'bg-blue-500/20 text-blue-400' :
                              analysis.device === 'tablet' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {analysis.device}
                            </span>
                          )}
                          {analysis.status === 'pending' && (
                            <>
                              {analysis.position && <span>#{analysis.position} {t('analysisQueue.inQueue')}</span>}
                              {analysis.estimated_wait_seconds && (
                                <span>~{formatWaitTime(analysis.estimated_wait_seconds)}</span>
                              )}
                            </>
                          )}
                          {analysis.status === 'running' && (
                            <>
                              <span className="text-blue-400">
                                {analysis.progress_percent !== undefined ? `${analysis.progress_percent}%` : t('analysisQueue.running')}
                              </span>
                              {analysis.estimated_remaining_seconds !== undefined && analysis.estimated_remaining_seconds > 0 && (
                                <span>{t('analysisQueue.remaining')} ~{formatWaitTime(analysis.estimated_remaining_seconds)}</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Header;
